import {useCallback, useEffect, useRef, useState} from 'react'
import {useAnalytics} from '@github-ui/use-analytics'
import type {Condition, RepositoryIdParameters} from '../../types/rules-types'
import {CREATE_PROPERTY_RULESET_BANNER_ANALYTICS} from './helpers'

const MIN_REPO_IDS_BANNER_THRESHOLD = 5
const MAX_REPO_IDS_BANNER_THRESHOLD = 30

interface CreatedProperty {
  propertyName: string
  // Number of repositories selected at the moment the property was created. Captured here so the
  // success banner can report it even after the ruleset target switches away from the id list.
  repositoryCount: number
}

type CreatePropertyBannerView = {kind: 'none'} | {kind: 'nudge'} | {kind: 'success'; createdProperty: CreatedProperty}

interface UseCreatePropertyBannerArgs {
  condition: Condition
}

interface UseCreatePropertyBannerResult {
  banner: CreatePropertyBannerView
  notifyPropertyCreated(property: CreatedProperty): void
}

export function useCreatePropertyBanner({condition}: UseCreatePropertyBannerArgs): UseCreatePropertyBannerResult {
  const {sendAnalyticsEvent} = useAnalytics()
  const [createdProperty, setCreatedProperty] = useState<CreatedProperty | null>(null)

  const kind = selectBanner({condition, hasCreatedProperty: createdProperty !== null})

  // Creating a property switches the target to `repository_property`, which keeps the success
  // banner eligible. As soon as the user changes the target or source type it becomes ineligible
  // (`kind` is no longer 'success'), so we clear the stored property during render — the success
  // banner disappears and, because the state is gone, cannot reappear if the user navigates back
  // to a property target.
  if (createdProperty !== null && kind !== 'success') {
    setCreatedProperty(null)
  }

  // Emit a single `shown` event the first time the experience becomes visible — regardless of
  // which banner (nudge or success) is showing. We only care whether the user saw the experience
  // at least once per mount, so the fire-once ref guard covers the whole experience.
  const isExperienceVisible = kind !== 'none'
  const hasTrackedShownEventRef = useRef(false)
  useEffect(() => {
    if (!isExperienceVisible || hasTrackedShownEventRef.current) return
    hasTrackedShownEventRef.current = true
    sendAnalyticsEvent(CREATE_PROPERTY_RULESET_BANNER_ANALYTICS.shown)
  }, [isExperienceVisible, sendAnalyticsEvent])

  const notifyPropertyCreated = useCallback((property: CreatedProperty) => {
    setCreatedProperty(property)
  }, [])

  let banner: CreatePropertyBannerView
  if (kind === 'success' && createdProperty !== null) {
    banner = {kind: 'success', createdProperty}
  } else if (kind === 'nudge') {
    banner = {kind: 'nudge'}
  } else {
    banner = {kind: 'none'}
  }

  return {banner, notifyPropertyCreated}
}

type BannerKind = 'none' | 'nudge' | 'success'

function selectBanner({
  condition,
  hasCreatedProperty,
}: {
  condition: Condition
  hasCreatedProperty: boolean
}): BannerKind {
  if (hasCreatedProperty && shouldRenderSuccessBanner(condition)) return 'success'
  if (shouldRenderNudgeBanner(condition)) return 'nudge'
  return 'none'
}

function shouldRenderNudgeBanner(condition: Condition): boolean {
  const isSupportedTarget = condition.target === 'repository_id'
  const repoIdCount = getRepositoryIdCount(condition)
  const isWithinThreshold = repoIdCount > MIN_REPO_IDS_BANNER_THRESHOLD && repoIdCount <= MAX_REPO_IDS_BANNER_THRESHOLD

  return isSupportedTarget && isWithinThreshold
}

function shouldRenderSuccessBanner(condition: Condition): boolean {
  return condition.target === 'repository_property'
}

function getRepositoryIdCount(condition: Condition): number {
  if (condition.target !== 'repository_id') return 0
  return (condition.parameters as RepositoryIdParameters).repository_ids.length
}
